import type {
  ChangePassword,
  CreateTenant,
  CreatedTenant,
  UpdateTenant,
  CoverageMatrix,
  GlobalFilters,
  LoginRequest,
  ProbeResult,
  PurgePreview,
  PurgeResult,
  RunList,
  RunSummary,
  StaffMe,
  TenantList,
  TenantSummary,
  UpdateProfile,
  RunDetail,
  TileQueryBatchResponse,
  TileQueryRequest,
  TileResult,
} from "@csbi/contracts"
import { del, get, patch, post } from "../../shared/api/request.js"

export const fetchMe = (signal?: AbortSignal) => get<StaffMe>("/auth/me", signal)

export const login = (body: LoginRequest) => post<StaffMe>("/auth/login", body)

export const logout = () => post<void>("/auth/logout", {})

export const updateProfile = (body: UpdateProfile) => patch<StaffMe>("/auth/profile", body)

export const changePassword = (body: ChangePassword) =>
  post<{ otherSessionsClosed: number }>("/auth/password", body)

export const fetchTenants = (signal?: AbortSignal) => get<TenantList>("/tenants", signal)

export const createTenant = (body: CreateTenant) => post<CreatedTenant>("/tenants", body)

export const updateTenant = (id: string, body: UpdateTenant) =>
  patch<TenantSummary>(`/tenants/${encodeURIComponent(id)}`, body)

export const deleteTenant = (id: string) => del(`/tenants/${encodeURIComponent(id)}`)

/** What a purge would destroy. Read-only; the dialog shows it before asking for confirmation */
export const fetchPurgePreview = (id: string, signal?: AbortSignal) =>
  get<PurgePreview>(`/tenants/${encodeURIComponent(id)}/purge-preview`, signal)

export const purgeTenant = (id: string, confirm: string) =>
  post<PurgeResult>(`/tenants/${encodeURIComponent(id)}/purge`, { confirm })

export const probeTenant = (id: string) =>
  post<ProbeResult>(`/tenants/${encodeURIComponent(id)}/probe`, {})

export const fetchCoverage = (signal?: AbortSignal) => get<CoverageMatrix>("/coverage", signal)

export const fetchRuns = (signal?: AbortSignal) => get<RunList>("/ops/runs", signal)

export const fetchRunDetail = (id: string, signal?: AbortSignal) =>
  get<RunDetail>(`/ops/runs/${encodeURIComponent(id)}`, signal)

export const triggerRun = (body: { tenantIds?: string[] } = {}) =>
  post<RunSummary>("/ops/runs", body)

export const queryTile = (body: TileQueryRequest, signal?: AbortSignal) =>
  post<TileResult>("/tiles/query", body, signal)

/**
 * ⚠ It returns an **array** whose entries succeed or fail individually, matched back **by index** — not by
 * `tileKey` (duplicate tileKeys are legal). This function used to be typed as `Record<string, TileResult>`, and
 * because it had no call site at all, that wrong type went unnoticed.
 */
export const queryTiles = (requests: readonly TileQueryRequest[], signal?: AbortSignal) =>
  post<TileQueryBatchResponse>("/tiles/query-batch", { requests }, signal)

export function buildTileRequest(
  tile: {
    tileKey: string
    metrics: readonly string[]
    splitBy?: readonly TileQueryRequest["splitBy"][number][]
    topN?: number
    granularity?: TileQueryRequest["granularity"]
  },
  filters: GlobalFilters,
  opts: { refresh?: boolean } = {},
): TileQueryRequest {
  return {
    tileKey: tile.tileKey,
    metrics: [...tile.metrics],
    tenantIds: [...filters.tenants],
    environments: [...filters.environments],
    includeChurned: filters.includeChurned,
    range: filters.range,
    granularity: tile.granularity ?? filters.granularity,
    splitBy: [...(tile.splitBy ?? ["period"])],
    ...(tile.topN ? { topN: tile.topN } : {}),
    comparePrevious: filters.comparePrevious,
    refresh: opts.refresh ?? false,
  }
}
